import { useState } from 'react';
import { HeartIcon } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { Button } from '@rocco/components/button';
import type { ButtonProps } from '@rocco/components/button/button';

interface TileFollowButtonProps extends Omit<ButtonProps, 'onClick'> {
    /** The item ID. */
    pinId: number;
    /** The pin type. */
    pinType:
        | 'ProjectPin'
        | 'ProductPin'
        | 'ProfessionalPin'
        | 'ArticlePin'
        | 'PhotoPin'
        | 'MemberUploadPin';
    /** The pin is already followed. Defaults to `false`. */
    isFollowed?: boolean;
    /** Callback when the button is clicked. Returns `true` if the pin is followed. */
    onFollow?: (
        pinId: number,
        pinType: TileFollowButtonProps['pinType']
    ) => Promise<boolean>;
}

/**
 * A button that allows users to follow a pin to a design board.
 */
export const TileFollowButton = ({
    pinId,
    pinType,
    isFollowed = false,
    onFollow,
    ...rest
}: TileFollowButtonProps) => {
    const [optimisticFollowed, setOptimisticFollowed] = useState(isFollowed);
    const isActive = isFollowed || optimisticFollowed;

    return (
        <Button
            type="button"
            size="square42"
            color="secondary70"
            {...rest}
            onClick={(e) => {
                // prevent from firing route event
                e.preventDefault();
                e.stopPropagation();
                if (onFollow) {
                    onFollow(pinId, pinType)
                        .then((isFollowedSuccess) => {
                            if (isFollowedSuccess) {
                                setOptimisticFollowed(true);
                            }
                        })
                        .catch(() => {});
                }
            }}
        >
            <HeartIcon
                className={cn(
                    'size-4',
                    isActive ? 'fill-gray-900' : 'stroke-gray-900'
                )}
            />
        </Button>
    );
};

export const mapPinType = (
    type: 'project' | 'product' | 'professional' | 'article'
) => {
    switch (type) {
        case 'project':
            return 'ProjectPin';
        case 'product':
            return 'ProductPin';
        case 'professional':
            return 'ProfessionalPin';
        case 'article':
            return 'ArticlePin';
    }
};
